import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PublicContentPage, { PublicCtaLink } from '../components/layout/PublicContentPage';

const VIDEO_SRC = '/videos/ablecomply-walkthrough.mp4';
const CAPTIONS_SRC = '/captions/ablecomply-walkthrough.en.vtt';
const DEMO_REQUEST_HREF = 'mailto:sales@ablecomply.com?subject=AbleComply%20Demo%20Request';

const WALKTHROUGH_TRANSCRIPT = [
  'Hey, I’ll give you a quick walkthrough of AbleComply and how accessibility consultants are using it in practice.',
  'The main idea behind AbleComply is to help consultants move from one-off accessibility audits to a more ongoing, structured compliance offering.',
  'Instead of delivering a report and moving on, you can provide continuous monitoring, reporting, and visibility for your clients.',
  'This is a workspace. You can think of this as one client or one portfolio.',
  'At a glance, you can see what is happening across their sites, scans, issues, and overall accessibility health.',
  'The key difference here is that this is not just a snapshot. It is continuously updated.',
  'So you always have something current to discuss with the client.',
  'Issues is where the actual findings come in.',
  'Instead of a static audit report, issues are categorized and prioritized, so you can guide clients on what matters most.',
  'You can also get context and evidence, so it is easier to explain the problem and what needs to be fixed.',
  'This becomes really helpful when you are working with clients over time. You are not redoing audits; you are tracking progress.',
  'Reports is one of the most important parts.',
  'Instead of sending a one-off PDF, you can give clients ongoing visibility into their compliance status.',
  'They can see progress, improvements, and outstanding issues without needing a full audit each time.',
  'From a consulting perspective, this shifts the conversation from one deliverable to an ongoing engagement.',
  'What I have seen is that a lot of accessibility work is still very project-based.',
  'With something like this, you can position accessibility more as an ongoing service with monitoring, reporting, and regular check-ins.',
  'Instead of doing an audit and finishing the engagement, you can stay involved and build something more recurring with clients.',
  'That is the high-level overview. I would be really curious how this fits with how you currently work.',
  'And whether something like this would be useful in your client engagements.',
  'Happy to dive deeper if it is helpful, but no pressure at all.',
] as const;

interface TimestampHint {
  time: string;
  label: string;
}

export default function WalkthroughPage() {
  const { t } = useTranslation('marketing');
  const [videoPending, setVideoPending] = useState(false);
  const highlightItems = t('pages.walkthrough.highlights.items', { returnObjects: true }) as string[];
  const timestampHints = t('pages.walkthrough.video.timestamps', { returnObjects: true }) as TimestampHint[];

  return (
    <PublicContentPage
      metaTitle={t('pages.walkthrough.meta.title')}
      metaDescription={t('pages.walkthrough.meta.description')}
      canonicalPath="/walkthrough"
      eyebrow={t('pages.walkthrough.hero.eyebrow')}
      title={t('pages.walkthrough.hero.title')}
      intro={t('pages.walkthrough.hero.intro')}
    >
      <section className="acc-public-page-card" aria-labelledby="walkthrough-highlights-heading">
        <div className="acc-public-page-meta">
          <div className="acc-public-page-meta-row">
            <span className="acc-public-page-meta-label">{t('pages.walkthrough.hero.metaLabel')}</span>
            <span className="acc-public-page-meta-value">AbleComply</span>
          </div>
        </div>

        <h2 id="walkthrough-highlights-heading">{t('pages.walkthrough.highlights.heading')}</h2>
        <ul className="acc-public-page-list" aria-label={t('pages.walkthrough.highlights.label')}>
          {highlightItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-card acc-public-walkthrough-card" aria-labelledby="walkthrough-video-heading">
        <h2 id="walkthrough-video-heading">{t('pages.walkthrough.video.heading')}</h2>
        <p id="walkthrough-video-description">{t('pages.walkthrough.video.description')}</p>

        <div className="acc-public-video-frame">
          {videoPending ? (
            <div className="acc-public-video-placeholder" role="status" aria-live="polite">
              {t('pages.walkthrough.video.pendingBadge')}
            </div>
          ) : (
            <video
              className="acc-public-video-player"
              controls
              preload="metadata"
              playsInline
              aria-describedby="walkthrough-video-description walkthrough-video-support"
              onError={() => setVideoPending(true)}
            >
              <source src={VIDEO_SRC} type="video/mp4" />
              <track
                kind="captions"
                src={CAPTIONS_SRC}
                srcLang="en"
                label="English captions"
                default
              />
              {t('pages.walkthrough.video.unsupported')}
            </video>
          )}
        </div>

        <p id="walkthrough-video-support" className="acc-public-video-note">
          {t('pages.walkthrough.video.supportingText')}
        </p>

        {videoPending && (
          <p className="acc-public-video-pending" role="status" aria-live="polite">
            {t('pages.walkthrough.video.pendingMessage')}
          </p>
        )}

        <section className="acc-public-walkthrough-transcript" aria-labelledby="walkthrough-transcript-heading">
          <h3 id="walkthrough-transcript-heading">Walkthrough transcript</h3>
          <div className="acc-public-walkthrough-transcript__body">
            {WALKTHROUGH_TRANSCRIPT.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </section>

        <div className="acc-public-walkthrough-timestamps-block">
          <h3>{t('pages.walkthrough.video.shortOnTime')}</h3>
          <ul className="acc-public-walkthrough-timestamps">
            {timestampHints.map((hint) => (
              <li key={`${hint.time}-${hint.label}`}>
                <span className="acc-public-walkthrough-timestamp-time">{hint.time}</span>
                <span>{hint.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="acc-public-page-card" aria-labelledby="walkthrough-positioning-heading">
        <h2 id="walkthrough-positioning-heading">{t('pages.walkthrough.positioning.heading')}</h2>
        <p>{t('pages.walkthrough.positioning.bodyOne')}</p>
        <p>{t('pages.walkthrough.positioning.bodyTwo')}</p>
      </section>

      <section className="acc-public-page-card" aria-labelledby="walkthrough-cta-heading">
        <h2 id="walkthrough-cta-heading">{t('pages.walkthrough.cta.title')}</h2>
        <p>{t('pages.walkthrough.cta.body')}</p>
        <div className="acc-public-page-actions">
          <PublicCtaLink href={DEMO_REQUEST_HREF} variant="primary">
            {t('actions.requestDemo')}
          </PublicCtaLink>
          <PublicCtaLink href="/" variant="secondary">
            {t('pages.walkthrough.cta.backHome')}
          </PublicCtaLink>
        </div>
      </section>
    </PublicContentPage>
  );
}
